export interface Article {
    readonly slug: string;
    readonly title: string;
    readonly publishedAt: string;
    readonly path: string;
}

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

export function parseIndex(value: unknown): readonly Article[] {
    if (!Array.isArray(value) || value.length === 0) {
        throw new Error("What's new index must contain at least one article");
    }

    const articles = value.map(parseArticle);
    const slugs = new Set(articles.map((article) => article.slug));
    if (slugs.size !== articles.length) {
        throw new Error("What's new index contains duplicate article slugs");
    }

    const expectedOrder = [...articles].sort(compareArticles);
    if (
        articles.some(
            (article, index) => article.slug !== expectedOrder[index]?.slug
        )
    ) {
        throw new Error("What's new index must be sorted by publication date");
    }

    return articles;
}

export function extractArticleBody(source: string): string {
    const match = /^---\r?\n[\s\S]*?\r?\n---(?:\r?\n|$)/.exec(source);
    if (!match) {
        throw new Error("What's new article is missing YAML frontmatter");
    }

    const body = source.slice(match[0].length);
    if (!body.trim()) {
        throw new Error("What's new article body must not be empty");
    }

    return body;
}

function parseArticle(value: unknown): Article {
    if (!isRecord(value)) {
        throw new Error("What's new index contains an invalid article");
    }

    const slug = readString(value, 'slug');
    const title = readString(value, 'title');
    const publishedAt = readString(value, 'publishedAt');
    const path = readString(value, 'path');

    if (!SLUG_PATTERN.test(slug)) {
        throw new Error(`Invalid What's new article slug: ${slug}`);
    }
    if (!title.trim()) {
        throw new Error(`What's new article ${slug} has no title`);
    }
    if (
        !ISO_DATE_PATTERN.test(publishedAt) ||
        !isCalendarValidIsoDate(publishedAt)
    ) {
        throw new Error(`What's new article ${slug} has an invalid date`);
    }

    const expectedPath = `/assets/content/whats-new/articles/${slug}/article.md`;
    if (path !== expectedPath) {
        throw new Error(`What's new article ${slug} has an invalid path`);
    }

    return { slug, title, publishedAt, path };
}

function isCalendarValidIsoDate(value: string): boolean {
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return false;

    const milliseconds = value.length === 24 ? Number(value.slice(20, 23)) : 0;
    return (
        parsedDate.getUTCFullYear() === Number(value.slice(0, 4)) &&
        parsedDate.getUTCMonth() + 1 === Number(value.slice(5, 7)) &&
        parsedDate.getUTCDate() === Number(value.slice(8, 10)) &&
        parsedDate.getUTCHours() === Number(value.slice(11, 13)) &&
        parsedDate.getUTCMinutes() === Number(value.slice(14, 16)) &&
        parsedDate.getUTCSeconds() === Number(value.slice(17, 19)) &&
        parsedDate.getUTCMilliseconds() === milliseconds
    );
}

function compareArticles(a: Article, b: Article): number {
    const dateOrder = Date.parse(b.publishedAt) - Date.parse(a.publishedAt);
    return dateOrder || compareAscii(a.slug, b.slug);
}

function compareAscii(left: string, right: string): number {
    if (left < right) return -1;
    if (left > right) return 1;
    return 0;
}

function readString(value: Record<string, unknown>, key: string): string {
    const field = value[key];
    if (typeof field !== 'string') {
        throw new TypeError(`What's new article is missing ${key}`);
    }
    return field;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}
